import axios from 'axios'
import { API_ENDPOINTS } from '../constants/apiEndpoints'
import type {
  JournalEntry,
  JournalItem,
  PaginatedResponse,
} from '../types/journal.types'
import type {
  CreateEntryPayload,
  UpdateEntryPayload,
} from '../types/entry.types'
import { BASE_URL } from '@/environment/environment'

export interface AttributeChoiceItem {
  id: string | number
  label: string
  children?: AttributeChoiceItem[]
  childrens?: AttributeChoiceItem[]
}

export interface AttributeChoicesPageResponse {
  next: string | null
  results: AttributeChoiceItem[]
}

class JournalsApi {
  private readonly baseUrl = `${BASE_URL}/api/journal`

  async getJournals(
    params: {
      search?: string
      page?: number
      cursor?: string
      page_size?: number
      departments?: string | string[]
      responsible_employees?: string | string[]
      creators?: string | string[]
      created_after?: string
      created_before?: string
      created_by?: string[]
    } = {},
  ) {
    const { data } = await axios.get<PaginatedResponse<JournalItem>>(
      `${BASE_URL}${API_ENDPOINTS.journals}/`,
      { params },
    )

    return data
  }

  async getJournal(id: number) {
    const { data } = await axios.get<JournalItem>(
      `${BASE_URL}${API_ENDPOINTS.journal(id)}/`,
    )

    return data
  }

  async createJournal(payload: Partial<JournalItem>) {
    const { data } = await axios.post<JournalItem>(
      `${BASE_URL}${API_ENDPOINTS.journals}/`,
      payload,
    )

    return data
  }

  async getJournalEntries(
    journalId: number,
    params: Record<string, string | number | string[] | undefined> = {},
  ) {
    const { data } = await axios.get<PaginatedResponse<JournalEntry>>(
      `${BASE_URL}${API_ENDPOINTS.journalEntries(journalId)}/`,
      { params },
    )

    return data
  }

  async getEntry(journalId: number, entryId: number) {
    const { data } = await axios.get<JournalEntry>(
      `${BASE_URL}${API_ENDPOINTS.journalEntry(journalId, entryId)}/`,
    )

    return data
  }

  async createEntry(journalId: number, payload: CreateEntryPayload) {
    const { data } = await axios.post<JournalEntry>(
      `${BASE_URL}${API_ENDPOINTS.journalEntries(journalId)}/`,
      payload,
    )

    return data
  }

  async updateEntry(
    journalId: number,
    entryId: number,
    payload: UpdateEntryPayload,
  ) {
    const { data } = await axios.put<JournalEntry>(
      `${BASE_URL}${API_ENDPOINTS.journalEntry(journalId, entryId)}/`,
      payload,
    )

    return data
  }

  async deleteEntry(journalId: number, entryId: number) {
    await axios.delete(
      `${BASE_URL}${API_ENDPOINTS.journalEntry(journalId, entryId)}/`,
    )
  }

  /** Первая страница choices атрибута; для следующей страницы — getAttributeChoicesByNext(next). */
  async getAttributeChoicesPage(
    journalId: number,
    templateAttribute: string | number,
    present = true,
    searchField?: string,
  ) {
    const params: Record<string, string | number> = {
      template_attribute: templateAttribute,
      present: present ? 'true' : 'false',
    }

    if (searchField) {
      params.search_field = searchField
    }

    const { data } = await axios.get<AttributeChoicesPageResponse>(
      `${BASE_URL}${API_ENDPOINTS.journalAttributeChoices(journalId)}`,
      { params },
    )

    return data
  }

  async getAttributeChoices(
    journalId: number,
    templateAttribute: string | number,
    present = true,
    searchField?: string,
  ) {
    const data = await this.getAttributeChoicesPage(
      journalId,
      templateAttribute,
      present,
      searchField,
    )

    return data.results
  }

  async getAttributeChoicesByNext(nextUrl: string) {
    const { data } = await axios.get<AttributeChoicesPageResponse>(nextUrl)

    return data
  }

  async getCreationAttributesList(journalId: number) {
    const { data } = await axios.get<{
      creation_template_attributes_ids: number[]
    }>(`${BASE_URL}${API_ENDPOINTS.journalAttributeCreationList(journalId)}`)

    return data
  }

  async attachFilesToEntry(
    journalId: number,
    entryId: number,
    files: Record<number, File | File[]>,
  ) {
    const formData = new FormData()

    Object.entries(files).forEach(([attributeId, fileOrFiles]) => {
      if (Array.isArray(fileOrFiles)) {
        fileOrFiles.forEach(file => formData.append(attributeId, file))
      } else {
        formData.append(attributeId, fileOrFiles)
      }
    })

    const { data } = await axios.post<JournalEntry>(
      `${BASE_URL}${API_ENDPOINTS.journalEntryAttachFile(journalId, entryId)}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return data
  }

  // Экспорт журнала
  async exportJournal(
    journalId: number,
    params: {
      group_by?: 'all' | 'monthly' | 'yearly'
      created_after?: string
      created_before?: string
    } = {},
  ) {
    const response = await axios.get(
      `${BASE_URL}${API_ENDPOINTS.journalExport(journalId)}/`,
      {
        params,
        responseType: 'blob',
      },
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')

    link.href = url

    const contentDisposition = response.headers['content-disposition']

    const fileName = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `journal_${journalId}_export.xlsx`

    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

export const journalsApi = new JournalsApi()
